import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { useEffect, useRef, useState } from 'react';
import { Tristate } from './tristateTask';

/**
 * Hook that manages the lifecycle of an async operation with loading and failure states.
 *
 * Returns a `status` value and a `run` function. Call `run(fn)` to execute an async
 * function; the status transitions automatically:
 *
 * - `NORMAL`   → idle, ready to accept a new call
 * - `SPINNING` → operation in progress (concurrent calls are ignored)
 * - `FAILURE`  → operation threw; resets to NORMAL after 2 seconds
 *
 * An optional `timeoutMs` can be passed to `run` to reject the promise if it takes
 * longer than the given duration.
 *
 * Cleanup is handled automatically on unmount: any pending reset timer is cleared and
 * in-flight abort signals are fired so stale state updates are suppressed.
 *
 * @example
 * ```tsx
 * const { status, run } = useAsyncAction();
 *
 * <Button
 *   loading={status === Tristate.SPINNING}
 *   onClick={() => run(saveData)}
 * />
 * ```
 */
export function useAsyncAction() {
  const [status, setStatus] = useState<Tristate>(Tristate.NORMAL);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current != null) clearTimeout(resetTimerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const run = async (fn: () => Promise<unknown>, timeoutMs?: number) => {
    if (status === Tristate.SPINNING) return;

    if (resetTimerRef.current != null) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }

    const abort = new AbortController();
    abortRef.current = abort;

    setStatus(Tristate.SPINNING);

    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise =
      timeoutMs != null
        ? new Promise<never>((_, reject) => {
            timeoutHandle = setTimeout(() => {
              reject(new Error(`AsyncButton timed out after ${timeoutMs}ms`));
            }, timeoutMs);
          })
        : null;

    try {
      if (timeoutPromise != null) {
        await Promise.race([fn(), timeoutPromise]);
      } else {
        await fn();
      }
      if (!abort.signal.aborted) setStatus(Tristate.NORMAL);
    } catch {
      if (!abort.signal.aborted) {
        setStatus(Tristate.FAILURE);
        resetTimerRef.current = setTimeout(() => {
          setStatus(Tristate.NORMAL);
          resetTimerRef.current = null;
        }, 2 * OneSecondInMillis);
      }
    } finally {
      if (timeoutHandle != null) clearTimeout(timeoutHandle);
    }
  };

  return { status, run };
}
